// src/components/Login.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSchoolConfig } from '../utils/domainConfig';
import { FaLock, FaEnvelope, FaSchool, FaUserGraduate, FaUserTie, FaIdBadge } from 'react-icons/fa';
import '../styles/Login.css';

function Login() {
  const [loginMode, setLoginMode] = useState('staff'); // 'staff' or 'student'
  const [email, setEmail] = useState('');
  const [rollNo, setRollNo] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn, studentSignIn } = useAuth();
  const navigate = useNavigate();
  const schoolConfig = getSchoolConfig();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loginMode === 'staff') {
      if (!email || !password) return setError('Please fill in all fields');
    } else {
      if (!rollNo || !password) return setError('Please fill in all fields');
    }

    try {
      setError('');
      setLoading(true);

      if (loginMode === 'student') {
        const { error } = await studentSignIn(parseInt(rollNo, 10), password);
        if (error) throw new Error(error.message);
      } else {
        const { error } = await signIn(email, password);
        if (error) throw new Error(error.message || 'Failed to sign in');
      }

      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to sign in. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setEmail('');
    setRollNo('');
    setPassword('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="school-icon">
            <FaSchool />
          </div>
          <h1>{schoolConfig.schoolName}</h1>
          <h2>{schoolConfig.subtitle}</h2>
        </div>

        <div className="login-tabs">
          <button
            className={`login-tab ${loginMode === 'staff' ? 'active' : ''}`}
            onClick={() => switchMode('staff')}
            type="button"
          >
            <FaUserTie /> Staff
          </button>
          <button
            className={`login-tab ${loginMode === 'student' ? 'active' : ''}`}
            onClick={() => switchMode('student')}
            type="button"
          >
            <FaUserGraduate /> Student
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          {loginMode === 'staff' ? (
            <div className="input-group">
              <div className="input-icon">
                <FaEnvelope />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="login-input"
              />
            </div>
          ) : (
            <div className="input-group">
              <div className="input-icon">
                <FaIdBadge />
              </div>
              <input
                type="number"
                value={rollNo}
                onChange={(e) => setRollNo(e.target.value)}
                required
                placeholder="Enter your Roll Number"
                className="login-input"
              />
            </div>
          )}
          
          <div className="input-group">
            <div className="input-icon">
              <FaLock />
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              className="login-input"
            />
          </div>
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Logging in...' : `Login as ${loginMode === 'staff' ? 'Staff' : 'Student'}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;